import { Injectable } from '@angular/core';
import { environment } from '@fantaz/shared/app-config';
import {
  CurrentUser,
  GAEventKeys,
  GoogleAnalyticsEvent,
  SmartlookInfo,
} from '@fantaz/shared/data-access/models';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Observable, Subject, from } from 'rxjs';
import { UAParser } from 'ua-parser-js';
declare global {
  interface Window {
    smartlook: { _debugData: SmartlookInfo };
    smartlookIntegrated: boolean;
    dynatrace: { sendBizEvent: unknown; sendEvent: unknown };
    clarity: { v: string };
  }
}
@Injectable({
  providedIn: 'root',
})
export class FzGoogleTagManagerService {
  gtmUnavailable = new Subject<boolean>();
  window = window;
  constructor(private gtmService: GoogleTagManagerService) {
    this.gtmUnavailable.next(false);
  }

  async pushTag(event: GoogleAnalyticsEvent): Promise<void> {
    try {
      const send = { ...event, timestamp: Date.now() };
      await this.gtmService
        .pushTag(send)
        .then(() => {
          this.gtmUnavailable.next(false);
        })
        .catch((error) => {
          console.error(error);
          this.gtmUnavailable.next(true);
        });
    } catch (error: any) {
      if (error) {
        const message = error?.message || 'Unknown error.';
        console.error(message);
        this.gtmUnavailable.next(true);
      }
    }
  }
  pushTagObs(event: any): Observable<void> {
    return from(this.gtmService.pushTag(event));
  }

  addGtmToDom() {
    try {
      this.gtmService.addGtmToDom();
    } catch (error) {
      console.error(error);
    }
  }

  loadAppEvent(currentUser?: CurrentUser) {
    const event = {
      event: GAEventKeys.USER_LOAD_APP,
      timestamp: new Date().getTime(),
      user_id: currentUser?.user.uuid || '',
      user_name: currentUser?.user.username || '',
      sponsor: currentUser?.user.sponsorUsername || '',
      env_name: environment.name,
    };
    if (currentUser) {
      const user = currentUser?.user;
      event.user_name = user?.username || '';
      event.sponsor = user?.sponsorUsername || '';
    }

    // *************************************************************************
    // ** Attaching PWA information to the event
    // *************************************************************************
    const parser = new UAParser();
    const device = parser.getDevice();

    const pwa: Record<string, any> = {};
    const url = new URL(window.location.href);

    // check if the app is running in PWA mode
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-ignore
      window.navigator.standalone === true || // safari patch
      url.searchParams.has('pwa')
    ) {
      const deviceType =
        window.innerWidth < 768
          ? 'mobile'
          : window.innerWidth < 1024
            ? 'tablet'
            : 'desktop';
      pwa['pwa'] = device.type || deviceType;
    }

    // check if app is opened from pwa menu
    if (url.searchParams.has('pwa_menu')) {
      pwa['pwa_menu'] = url.searchParams.get('pwa_menu');
    }

    this.pushTag({
      ...event,
      ...pwa,
    });
  }

  isGTMBlocked(): Subject<boolean> {
    return this.gtmUnavailable;
  }

  // Function to get Smartlook information
  getSmartlookInfo(): SmartlookInfo | null {
    // Access the Smartlook debug data from the 'smartlook' object
    if (this.window.smartlookIntegrated === true) {
      return window.smartlook?._debugData || null;
    }
    return null;
  }

  // Function to get Clarity information
  getClarityInfo(): boolean {
    // Access the Clarity object
    const version = window.clarity?.v;
    return version ? true : false;
  }
}
